import React, { useState, useCallback } from 'react';
import { assessmentTypeLabel } from './classifyAssessment';

/**
 * UpcomingAssessmentsList — lives inside the HealthDrawer.
 * Renders AssessmentGroup rows (each group may contain 1+ merged items).
 * Groups with multiple items are expandable.
 *
 * Props:
 *   assessments  — sorted AssessmentGroup[] from useUpcomingAssessments
 *   totalCount   — total group count
 *   onItemClick  — (item) => void — navigates to assignment/event
 *   defaultVisible — how many groups to show before "Show more" (default 4)
 */
const DEFAULT_VISIBLE = 4;

export default React.memo(function UpcomingAssessmentsList({
  assessments = [],
  totalCount = 0,
  onItemClick,
  defaultVisible = DEFAULT_VISIBLE,
}) {
  const [expanded, setExpanded] = useState(false);

  if (assessments.length === 0) return null;

  const visible = expanded ? assessments : assessments.slice(0, defaultVisible);
  const remaining = assessments.length - defaultVisible;

  return (
    <div className="hd-assessmentsCard">
      <div className="hd-assessmentsHeader">
        <h3 className="hd-sectionLabel">Upcoming Assessments</h3>
        {remaining > 0 && !expanded && (
          <button
            className="hd-assessmentsViewAll"
            onClick={() => setExpanded(true)}
            type="button"
          >
            View all
          </button>
        )}
        {expanded && remaining > 0 && (
          <button
            className="hd-assessmentsViewAll"
            onClick={() => setExpanded(false)}
            type="button"
          >
            Show less
          </button>
        )}
      </div>

      <div className="hd-assessmentsList">
        {visible.map((group) => (
          <AssessmentGroupRow
            key={group.id}
            group={group}
            onItemClick={onItemClick}
          />
        ))}
      </div>

      {!expanded && remaining > 0 && (
        <button
          className="hd-assessmentsMore"
          onClick={() => setExpanded(true)}
          type="button"
        >
          +{remaining} more assessment{remaining !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
});

/* ─── Assessment group row — always navigates on click ─── */
function AssessmentGroupRow({ group, onItemClick }) {
  const handleClick = useCallback(() => {
    // Always navigate directly to the best item (prefer event source for modal)
    const bestItem = group.items?.find(i => i.source === 'event') || group.items?.[0] || group;
    if (onItemClick) onItemClick(bestItem);
  }, [onItemClick, group]);

  const relTime = group.daysUntil === 0
    ? 'Today'
    : group.daysUntil === 1
      ? 'Tomorrow'
      : `in ${group.daysUntil}d`;

  const badgeClass = `hd-asBadge hd-asBadge--${group.type || 'default'}`;

  return (
    <div className="hd-asGroup">
      <button
        className="hd-assessmentRow"
        onClick={handleClick}
        type="button"
      >
        {/* Left accent bar */}
        <span className="hd-asAccent" aria-hidden="true" />

        {/* Type badge */}
        <span className={badgeClass}>
          {assessmentTypeLabel(group.type)}
        </span>

        {/* Title + course */}
        <span className="hd-asBody">
          <span className="hd-asTitle">{group.title}</span>
          <span className="hd-asCourse">{group.courseName || group.courseCode || ''}</span>
        </span>

        {/* Date + relative */}
        <span className="hd-asDate">
          <span className="hd-asDateAbs">{group.dateLabel}</span>
          <span className="hd-asDateRel">{relTime}</span>
        </span>

        {/* Navigate chevron */}
        <span className="hd-asChevron" aria-hidden="true">&rsaquo;</span>
      </button>
    </div>
  );
}


